import cn from 'classnames';
import { forwardRef, useMemo } from 'react';

import PaginationContext from '../pagination-context';
import { PaginationItemsPerPageSelector } from '../pagination-items-per-page-selector';
import { PaginationNavigation } from '../pagination-navigation';
import { PaginationSummary } from '../pagination-summary';

import type { ForwardedRef, ReactElement } from 'react';

import type { PaginationContextValue } from '../pagination-context';
import type { PaginationProps } from './pagination.types';

import './pagination.scss';

/**
 * Pagination component to display pagination links and manage the number of items per page
 *
 * ### Usage
 * ```tsx
 * import { Pagination } from '@bloomreach/react-banana-ui'
 *
 * <Pagination
 *   count={100}
 *   itemsPerPage={10}
 *   page={0}
 *   onPageChange={(page) => console.log(page)}
 *   itemsPerPageOptions={[10, 25, 50]}
 *   onItemsPerPageChange={(num) => console.log(num)}
 * />
 * ```
 */
const Pagination = forwardRef(
  (props: PaginationProps, forwardedRef: ForwardedRef<HTMLDivElement>): null | ReactElement => {
    const { className, count, itemsPerPage, itemsPerPageOptions, onItemsPerPageChange, onPageChange, page, ...rest } =
      props;

    const contextValue: PaginationContextValue = useMemo(
      () => ({
        count,
        itemsPerPage,
        itemsPerPageOptions,
        onItemsPerPageChange,
        onPageChange,
        page,
      }),
      [count, itemsPerPage, itemsPerPageOptions, onItemsPerPageChange, onPageChange, page],
    );

    if (count < 1) {
      return null;
    }

    return (
      <PaginationContext.Provider value={contextValue}>
        <div {...rest} className={cn('rbui-pagination', className)} ref={forwardedRef}>
          <div className="rbui-pagination__left">
            <PaginationItemsPerPageSelector />
            <PaginationSummary />
          </div>
          <PaginationNavigation />
        </div>
      </PaginationContext.Provider>
    );
  },
);

Pagination.displayName = 'Pagination';

export default Pagination;
